/**
 * The full "Master Sheet" nutrient table: 94 rows in six groups, indented by
 * parent, with a source badge on each value (USDA / Calc / AI / You / Label)
 * and "—" for anything unknown. Groups collapse; the open/closed state is
 * remembered per browser. With `targets`, each row also gets a bar and a %
 * of the day's DRI target (green = met, amber = over a limit, red = over a UL).
 */
import { useState, type ReactElement } from 'react';
import { GROUPS, NUTRIENT_BY_KEY, NUTRIENTS, type NutrientDef, type NutrientGroup } from '@engine/nutrients';
import { targetStatus, type NutrientTarget, type TargetStatus } from '@engine/dri';
import { netCarbs } from '../lib/nutrition';
import { SOURCE_BADGE } from '../lib/foodNutrients';
import type { NutrientMap, NutrientSourceMap } from '../lib/types';
import { Icon } from './Icon';

const OPEN_KEY: string = 'mightypeanut.nutrientGroups';

function readOpen(defaults: NutrientGroup[]): Set<NutrientGroup> {
  try {
    const raw: string | null = localStorage.getItem(OPEN_KEY);
    if (raw) return new Set(JSON.parse(raw) as NutrientGroup[]);
  } catch { /* storage unavailable */ }
  return new Set(defaults);
}

function depth(def: NutrientDef): number {
  let d: number = 0;
  let p: string | null = def.parent;
  while (p) { d++; p = NUTRIENT_BY_KEY.get(p)?.parent ?? null; }
  return d;
}

export function formatAmount(v: number, unit: string): string {
  const abs: number = Math.abs(v);
  const digits: number = unit === 'kcal' || unit === 'IU' ? 0 : abs >= 100 ? 0 : abs >= 10 ? 1 : abs >= 1 ? 2 : abs === 0 ? 0 : 3;
  return v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function TargetCell({ amount, target, unit }: { amount: number | undefined; target: NutrientTarget | undefined; unit: string }): ReactElement {
  if (!target) return <td className="tgt" />;
  const status: TargetStatus = targetStatus(amount, target);
  const pct: number = amount === undefined ? 0 : (amount / target.amount) * 100;
  const title: string = `${target.isLimit ? 'Limit' : target.kind}: ${target.isLimit ? 'under ' : ''}${formatAmount(target.amount, unit)} ${unit}`
    + (target.ul !== null ? ` · upper limit ${formatAmount(target.ul, unit)} ${unit}${target.ulSupplementOnly ? ' (supplements only)' : ''}` : '')
    + (target.note ? ` · ${target.note}` : '');
  return (
    <td className="tgt" title={title}>
      <span className={`dbar st-${status}${target.isLimit ? ' limit' : ''}`}><b style={{ width: `${Math.min(100, pct)}%` }} /></span>
      <span className={`dpct st-${status}`}>{amount === undefined ? '—' : `${Math.round(pct)}%`}</span>
    </td>
  );
}

export function NutrientPanel({ values, sources, targets, showSources = true, defaultOpen = ['general', 'carbohydrates', 'lipids', 'protein', 'vitamins', 'minerals'] }: {
  values: NutrientMap;
  sources?: NutrientSourceMap;
  /** Daily DRI targets by catalog key (Diary only). */
  targets?: ReadonlyMap<string, NutrientTarget>;
  showSources?: boolean;
  defaultOpen?: NutrientGroup[];
}): ReactElement {
  const [open, setOpen] = useState<Set<NutrientGroup>>(() => readOpen(defaultOpen));
  const toggle = (g: NutrientGroup): void => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g); else next.add(g);
      try { localStorage.setItem(OPEN_KEY, JSON.stringify([...next])); } catch { /* storage unavailable */ }
      return next;
    });
  };

  return (
    <div className="npanel">
      {GROUPS.map((g) => {
        const rows: NutrientDef[] = NUTRIENTS.filter((n) => n.group === g.key);
        const known: number = rows.filter((r) => values[r.key] !== undefined).length;
        const withTarget: NutrientDef[] = targets ? rows.filter((r) => targets.has(r.key)) : [];
        const met: number = withTarget.filter((r) => targetStatus(values[r.key], targets!.get(r.key)) === 'met').length;
        const isOpen: boolean = open.has(g.key);
        return (
          <section key={g.key} className="npanel-group">
            <button type="button" className="npanel-head" onClick={() => toggle(g.key)} aria-expanded={isOpen}>
              <Icon name={isOpen ? 'chevD' : 'chevR'} size={16} />
              <span>{g.title}</span>
              <span className="npanel-count">{withTarget.length > 0 ? <><b className="good">{met}/{withTarget.length}</b> targets met · </> : null}{known}/{rows.length}</span>
            </button>
            {isOpen && (
              <table className="ntable">
                <tbody>
                  {rows.map((def) => {
                    const v: number | undefined = values[def.key];
                    const src: string | undefined = sources?.[def.key];
                    const badge = src ? SOURCE_BADGE[src] : undefined;
                    return (
                      <tr key={def.key} className={v === undefined ? 'missing' : ''}>
                        <td style={{ paddingLeft: depth(def) * 16 }} className={depth(def) > 0 ? 'sub' : ''}>{def.name}</td>
                        <td className="v num">{v === undefined ? '—' : `${formatAmount(v, def.unit)} ${def.unit}`}</td>
                        {targets && <TargetCell amount={v} target={targets.get(def.key)} unit={def.unit} />}
                        {showSources && <td className="src">{badge && v !== undefined ? <span className={`sbadge s-${src}`} title={badge.title}>{badge.label}</span> : null}</td>}
                      </tr>
                    );
                  })}
                  {g.key === 'carbohydrates' && values.carbs_g !== undefined && (
                    <tr className="derived">
                      <td style={{ paddingLeft: 16 }} className="sub">Net Carbs <span className="faint">(total − fiber)</span></td>
                      <td className="v num">{formatAmount(netCarbs(values.carbs_g, values.fiber_g), 'g')} g</td>
                      {targets && <td className="tgt" />}
                      {showSources && <td className="src" />}
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </section>
        );
      })}
    </div>
  );
}
